import { TrendingUp, IndianRupee, Trophy } from "lucide-react";
import Reveal from "@/components/Reveal";

const SLABS = [
  {
    slab: "Slab 1",
    range: "₹5L – ₹10L",
    commission: "2.00%",
    salary: "₹10,000",
    accent: "from-[#1B3F7A]/40",
  },
  {
    slab: "Slab 2",
    range: "₹10L – ₹1 Cr",
    commission: "2.50%",
    salary: "₹20,000",
    accent: "from-[#1B3F7A]/55",
  },
  {
    slab: "Slab 3",
    range: "₹1 Cr – ₹5 Cr",
    commission: "3.00%",
    salary: "₹30,000",
    accent: "from-[#D4AF37]/15",
  },
  {
    slab: "Slab 4",
    range: "₹5 Cr – ₹25 Cr",
    commission: "4.00%",
    salary: "₹50,000",
    accent: "from-[#D4AF37]/30",
    highlight: true,
  },
  {
    slab: "Slab 5",
    range: "₹25 Cr – ₹50 Cr",
    commission: "5.00%",
    salary: "₹80,000",
    accent: "from-[#D4AF37]/45",
  },
];

export default function DSASlabs() {
  return (
    <section
      id="dsa-slabs"
      data-testid="dsa-slabs-section"
      className="relative bg-[#04102B] text-white py-24 md:py-32 overflow-hidden noise-overlay"
    >
      <div className="pointer-events-none absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full border border-[#D4AF37]/10" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/8 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <span className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37]">
                · DSA payout slabs
              </span>
              <h2
                data-testid="dsa-heading"
                className="font-display text-[40px] md:text-[60px] mt-3 leading-[1.05] tracking-tight"
              >
                Earn up to <em className="not-italic text-[#D4AF37]">5%</em> + ₹80,000 / month.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/70 leading-relaxed">
                A transparent 5-tier commission structure for our associates &amp; DSAs — designed
                to reward consistent performance with both commission and a monthly fixed salary.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Slab cards */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {SLABS.map((s, i) => (
            <Reveal key={s.slab} delay={i * 70}>
              <div
                data-testid={`dsa-slab-${i + 1}`}
                className={`relative h-full rounded-2xl p-6 border ${
                  s.highlight
                    ? "border-[#D4AF37]/80 bg-gradient-to-b from-[#D4AF37]/20 to-transparent"
                    : "border-[#D4AF37]/25 bg-gradient-to-b " + s.accent + " to-transparent"
                } hover:-translate-y-1 hover:border-[#D4AF37]/70 transition-all duration-300 overflow-hidden`}
              >
                {s.highlight && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] tracking-wider uppercase text-[#0A1F44] bg-[#D4AF37] rounded-full px-2 py-0.5 font-semibold">
                    <Trophy size={10} /> Popular
                  </span>
                )}
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37]/85">
                  {s.slab}
                </div>
                <div className="mt-2 text-white/65 text-sm leading-tight">
                  Monthly disbursal
                </div>
                <div className="mt-1 font-display text-xl text-white">{s.range}</div>

                <div className="mt-5 h-px bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />

                <div className="mt-5 space-y-3">
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-white/50">Commission</div>
                    <div className="font-display text-3xl text-[#F0D788] flex items-baseline">
                      {s.commission}
                      <TrendingUp size={14} className="ml-1 text-[#D4AF37]" />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-white/50">+ Monthly Salary</div>
                    <div className="font-display text-2xl text-white flex items-center">
                      <IndianRupee size={16} className="text-[#D4AF37] mr-0.5" />
                      {s.salary.replace("₹", "")}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Eligibility footer */}
        <Reveal delay={300}>
          <div className="mt-12 grid md:grid-cols-3 gap-4 text-center md:text-left">
            <div
              data-testid="dsa-eligibility-1"
              className="rounded-xl p-5 border border-[#D4AF37]/25 bg-white/[0.03]"
            >
              <div className="text-[11px] tracking-wider uppercase text-[#D4AF37]">Who can join</div>
              <div className="mt-1 text-white/85 text-sm">
                Anyone aged 18–60 with minimum 10<sup>th</sup> pass. Freshers welcome.
              </div>
            </div>
            <div
              data-testid="dsa-eligibility-2"
              className="rounded-xl p-5 border border-[#D4AF37]/25 bg-white/[0.03]"
            >
              <div className="text-[11px] tracking-wider uppercase text-[#D4AF37]">Ideal profiles</div>
              <div className="mt-1 text-white/85 text-sm">
                Insurance agents, students, businessmen, shop owners, freelance advisors.
              </div>
            </div>
            <div
              data-testid="dsa-eligibility-3"
              className="rounded-xl p-5 border border-[#D4AF37]/25 bg-white/[0.03]"
            >
              <div className="text-[11px] tracking-wider uppercase text-[#D4AF37]">What you get</div>
              <div className="mt-1 text-white/85 text-sm">
                Training, lead support, CRM tools, recognition &amp; no-limit income.
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={400}>
          <div className="mt-10 flex justify-center">
            <a
              data-testid="dsa-cta"
              href="/apply/dsa"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold text-sm font-semibold"
            >
              Become a DSA Today
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
